/**
 * LandingPageTab Component
 * Admin tab for managing event registration landing pages
 * Simplified: no HTML template editor or field config — form fields are standardized
 */

import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EyeIcon,
  PlusIcon,
  TrashIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import Button from "../../common/Button";
import LoadingSpinner from "../../common/LoadingSpinner";
import RichTextEditor from "../../content/RichTextEditor";
import { useToast } from "../../ui/Toast";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getLandingPageByEventId,
  createLandingPage,
  updateLandingPage,
  deleteLandingPage,
  generateSlug,
  isSlugAvailable,
  setLandingPagePublished,
  getRegistrationList,
  setRegistrationList,
  clearRegistrationList,
  createRegistrationList,
  listStaticLists,
} from "../../../services/eventLandingPage.service";
import { updateEvent } from "../../../services/events.service";
import {
  evaluatePublishReadiness,
  type PublishStatus,
} from "../../../utils/eventLandingPage";
import { getRegistrationDefault } from "../../../utils/eventTypeRegistrationDefaults";
import type { Event } from "../../../types/event.types";

interface LandingPageTabProps {
  event: Event;
}

const STATUS_META: Record<PublishStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-gray-100 text-gray-700" },
  ready: { label: "Ready to publish", cls: "bg-blue-100 text-blue-800" },
  published: { label: "Published — live", cls: "bg-green-100 text-green-800" },
  published_unavailable: {
    label: "Published — event unavailable",
    cls: "bg-amber-100 text-amber-900",
  },
};

const LandingPageTab: React.FC<LandingPageTabProps> = ({ event }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Form state
  const [slug, setSlug] = useState("");
  const [landingPageTitle, setLandingPageTitle] = useState("");
  const [eventDetailsHtml, setEventDetailsHtml] = useState("");
  const [contactDepartment, setContactDepartment] =
    useState("Events Department");
  const [contactPhone, setContactPhone] = useState("855-379-8994");
  const [contactEmail, setContactEmail] = useState("Seminars@USLawShield.com");
  const [registrationLimit, setRegistrationLimit] = useState("");
  const [memberPrice, setMemberPrice] = useState("");
  const [nonMemberPrice, setNonMemberPrice] = useState("");
  const [pricingLabel, setPricingLabel] = useState("");
  const [hearAboutOptions, setHearAboutOptions] = useState<string[]>([
    "Google Search",
    "Social Media",
    "Friend/Family",
    "Email",
    "Event Flyer",
    "Radio/TV",
    "Other",
  ]);
  const [newHearOption, setNewHearOption] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Public walk-in member self-check-in (events.public_self_checkin_enabled). Independent
  // of the landing page. NOTE (2026-09-09): the per-event /event-self-checkin/:eventId page it
  // used to gate is retired (that route now redirects to /self-checkin), and the universal
  // /self-checkin page does NOT read this column. The flag is still read by
  // self_checkin_attend_and_stamp / stamp_self_checkin_receipt on the retired edge-function
  // path; retiring the edge fn and the column is a separate decision, so the toggle stays.
  const [selfCheckinEnabled, setSelfCheckinEnabled] = useState(
    !!event.public_self_checkin_enabled,
  );
  const [selfCheckinSaving, setSelfCheckinSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // MVP: invitees are members. Stored in field_config.audience_type ('members'|'public').
  const [audienceType, setAudienceType] = useState<"members" | "public">(
    "members",
  );
  // Operator explicitly chose NOT to add registrants to a list (publish needs this ack).
  const [noListAck, setNoListAck] = useState(false);
  const [selectedListId, setSelectedListId] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isListBusy, setIsListBusy] = useState(false);

  // Fetch existing landing page
  const {
    data: landingPage,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["eventLandingPage", event.id],
    queryFn: () => getLandingPageByEventId(event.id),
  });

  // Registration list (event_list_associations type 'registered') + static-list options
  const { data: registrationList } = useQuery({
    queryKey: ["eventRegistrationList", event.id],
    queryFn: () => getRegistrationList(event.id),
  });
  const { data: staticLists } = useQuery({
    queryKey: ["staticLists"],
    queryFn: () => listStaticLists(),
  });

  // Populate form when data loads
  useEffect(() => {
    setRegistrationLimit(event.max_attendees?.toString() || "");
    if (landingPage) {
      setSlug(landingPage.slug);
      setLandingPageTitle(landingPage.landing_page_title || "");
      setEventDetailsHtml(landingPage.event_details_html || "");
      setContactDepartment(
        landingPage.contact_department || "Events Department",
      );
      setContactPhone(landingPage.contact_phone || "855-379-8994");
      setContactEmail(landingPage.contact_email || "Seminars@USLawShield.com");
      setMemberPrice(landingPage.member_price?.toString() || "");
      setNonMemberPrice(landingPage.non_member_price?.toString() || "");
      setPricingLabel(landingPage.pricing_label || "");
      setHearAboutOptions(
        landingPage.hear_about_options || [
          "Google Search",
          "Social Media",
          "Friend/Family",
          "Email",
          "Event Flyer",
          "Radio/TV",
          "Other",
        ],
      );
      setIsPublished(landingPage.is_published);
      const fc = (landingPage.field_config || {}) as {
        audience_type?: string;
        no_list_ack?: boolean;
      };
      setAudienceType(fc.audience_type === "public" ? "public" : "members");
      setNoListAck(Boolean(fc.no_list_ack));
    } else if (!isLoading) {
      setSlug(generateSlug(event.title));
    }
  }, [landingPage, isLoading, event.title, event.max_attendees]);

  // Save handler
  const handleSave = async () => {
    if (!user?.id) {
      return;
    }
    if (!slug.trim()) {
      showToast("error", "Slug is required");
      return;
    }

    setIsSaving(true);
    try {
      const available = await isSlugAvailable(slug, landingPage?.id);
      if (!available) {
        showToast(
          "error",
          "This slug is already in use. Please choose another.",
        );
        setIsSaving(false);
        return;
      }

      const payload = {
        slug: slug.trim(),
        html_template: "",
        field_config: { audience_type: audienceType, no_list_ack: noListAck },
        landing_page_title: landingPageTitle || null,
        event_details_html: eventDetailsHtml || null,
        contact_department: contactDepartment || "Events Department",
        contact_phone: contactPhone || "855-379-8994",
        contact_email: contactEmail || "Seminars@USLawShield.com",
        member_price: memberPrice ? parseFloat(memberPrice) : null,
        non_member_price: nonMemberPrice ? parseFloat(nonMemberPrice) : null,
        pricing_label: pricingLabel || null,
        hear_about_options: hearAboutOptions,
        success_message: null,
        is_published: isPublished,
      };

      if (landingPage) {
        await updateLandingPage(landingPage.id, payload, user.id);
        showToast("success", "Landing page updated");
      } else {
        await createLandingPage({ ...payload, event_id: event.id }, user.id);
        showToast("success", "Landing page created");
      }

      // Sync event settings that a landing page implies
      const eventUpdates: Record<string, unknown> = {};

      // Registration limit
      const newLimit = registrationLimit ? parseInt(registrationLimit) : null;
      if (newLimit !== (event.max_attendees || null)) {
        eventUpdates.max_attendees = newLimit;
      }

      // Landing page requires registration required and non-public event
      if (!event.registration_required) {
        eventUpdates.registration_required = true;
      }
      if (event.is_public) {
        eventUpdates.is_public = false;
      }

      if (Object.keys(eventUpdates).length > 0) {
        await updateEvent(event.id, eventUpdates as any);
        queryClient.invalidateQueries({ queryKey: ["events", event.id] });
      }

      queryClient.invalidateQueries({
        queryKey: ["eventLandingPage", event.id],
      });
    } catch (err) {
      console.error("Error saving landing page:", err);
      showToast("error", "Failed to save landing page");
    } finally {
      setIsSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!landingPage) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteLandingPage(landingPage.id);
      showToast("success", "Landing page deleted");
      queryClient.invalidateQueries({
        queryKey: ["eventLandingPage", event.id],
      });
      setShowDeleteConfirm(false);
      setSlug(generateSlug(event.title));
      setLandingPageTitle("");
      setEventDetailsHtml("");
      setContactDepartment("Events Department");
      setContactPhone("855-379-8994");
      setContactEmail("Seminars@USLawShield.com");
      setMemberPrice("");
      setNonMemberPrice("");
      setPricingLabel("");
      setIsPublished(false);
    } catch (err) {
      console.error("Error deleting landing page:", err);
      showToast("error", "Failed to delete landing page");
    } finally {
      setIsDeleting(false);
    }
  };

  // Add hear about option
  const handleAddHearOption = () => {
    const trimmed = newHearOption.trim();
    if (trimmed && !hearAboutOptions.includes(trimmed)) {
      setHearAboutOptions((prev) => [...prev, trimmed]);
      setNewHearOption("");
    }
  };

  // Remove hear about option
  const handleRemoveHearOption = (index: number) => {
    setHearAboutOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const fullUrl = landingPage
    ? `${window.location.origin}/register/${landingPage.id}`
    : "";
  const previewUrl = landingPage ? `${fullUrl}?preview=true` : "";

  // Publish readiness for the status panel + the Publish gate.
  const readiness = evaluatePublishReadiness({
    slug,
    isPublished,
    eventTitle: event.title,
    eventStatus: event.status ?? event.event_status,
    registrationOpen: event.registration_open,
    hasStartTime: Boolean(event.start_time),
    hasRegistrationList: Boolean(registrationList),
    noListAck,
  });

  const handlePublish = async () => {
    if (!user?.id || !landingPage) {
      showToast("error", "Save the page first, then publish.");
      return;
    }
    if (readiness.blockers.length > 0) {
      showToast("error", readiness.blockers[0]);
      return;
    }
    setIsPublishing(true);
    try {
      // Persist the no-list ack alongside is_published so publishing without a
      // prior Save still records the operator's choice durably (audit finding #1).
      await updateLandingPage(
        landingPage.id,
        {
          is_published: true,
          field_config: { audience_type: audienceType, no_list_ack: noListAck },
        },
        user.id,
      );
      setIsPublished(true);
      showToast("success", "Page published — the live URL is now active.");
      queryClient.invalidateQueries({
        queryKey: ["eventLandingPage", event.id],
      });
    } catch (err) {
      console.error("Publish error:", err);
      showToast("error", "Failed to publish page");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!user?.id || !landingPage) {
      return;
    }
    setIsPublishing(true);
    try {
      await setLandingPagePublished(landingPage.id, false, user.id);
      setIsPublished(false);
      showToast(
        "success",
        "Page unpublished — the live URL no longer accepts registrations.",
      );
      queryClient.invalidateQueries({
        queryKey: ["eventLandingPage", event.id],
      });
    } catch (err) {
      console.error("Unpublish error:", err);
      showToast("error", "Failed to unpublish page");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleToggleSelfCheckin = async () => {
    const next = !selfCheckinEnabled;
    setSelfCheckinSaving(true);
    try {
      await updateEvent(event.id, { public_self_checkin_enabled: next });
      setSelfCheckinEnabled(next);
      showToast(
        "success",
        next
          ? "Public member self check-in enabled."
          : "Public member self check-in disabled.",
      );
      queryClient.invalidateQueries({ queryKey: ["event", event.id] });
    } catch (err) {
      console.error("Self check-in toggle error:", err);
      showToast("error", "Failed to update self check-in setting");
    } finally {
      setSelfCheckinSaving(false);
    }
  };

  // Share the universal public self-check-in page (#1a, 2026-08-12). The old per-event
  // /event-self-checkin/:eventId page was one of several divergent check-in pages that confused
  // testers; /self-checkin is the single always-on page (it lists today's events, filtered by
  // state/date, so the attendee picks this event there).
  const selfCheckinUrl = `${window.location.origin}/self-checkin`;

  const handleSelectExistingList = async (listId: string) => {
    if (!user?.id || !listId) {
      return;
    }
    setIsListBusy(true);
    try {
      await setRegistrationList(event.id, listId, user.id);
      setNoListAck(false);
      showToast("success", "Registration list set.");
      queryClient.invalidateQueries({
        queryKey: ["eventRegistrationList", event.id],
      });
    } catch (err) {
      console.error("Set list error:", err);
      showToast("error", "Failed to set registration list");
    } finally {
      setIsListBusy(false);
    }
  };

  const handleCreateList = async () => {
    if (!user?.id) {
      return;
    }
    setIsListBusy(true);
    try {
      const name = `${event.title} Registrants`;
      await createRegistrationList(event.id, name, user.id);
      setNoListAck(false);
      showToast("success", `Created list "${name}".`);
      queryClient.invalidateQueries({
        queryKey: ["eventRegistrationList", event.id],
      });
      queryClient.invalidateQueries({ queryKey: ["staticLists"] });
    } catch (err) {
      console.error("Create list error:", err);
      showToast("error", "Failed to create registration list");
    } finally {
      setIsListBusy(false);
    }
  };

  const handleClearList = async () => {
    if (!user?.id) {
      return;
    }
    setIsListBusy(true);
    try {
      await clearRegistrationList(event.id);
      showToast("success", "Registration list removed.");
      queryClient.invalidateQueries({
        queryKey: ["eventRegistrationList", event.id],
      });
    } catch (err) {
      console.error("Clear list error:", err);
      showToast("error", "Failed to remove registration list");
    } finally {
      setIsListBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4">
        Failed to load landing page data. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Registration Landing Page
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure the public registration page for this event. The page uses
            standardized USLS branding and form fields.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {landingPage && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : landingPage
                ? "Save Changes"
                : "Create Landing Page"}
          </Button>
        </div>
      </div>

      {/* Slug */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Slug (internal identifier)
        </label>
        <input
          type="text"
          value={slug}
          onChange={(e) =>
            setSlug(
              e.target.value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, "-")
                .replace(/-+/g, "-"),
            )
          }
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="event-slug"
        />
        {fullUrl && (
          <p className="text-xs text-gray-400 mt-2">
            Public URL:{" "}
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              {fullUrl}
            </a>
          </p>
        )}
      </div>

      {/* Landing Page Title */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Landing Page Title
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Overrides the event title on the public page. Leave blank to use the
          event title.
        </p>
        <input
          type="text"
          value={landingPageTitle}
          onChange={(e) => setLandingPageTitle(e.target.value)}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={event.title}
        />
      </div>

      {/* Event Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Event Details
        </label>
        {getRegistrationDefault(event.type)?.locked ? (
          <p className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
            This event type uses a standard, locked registration-page layout — a
            fixed banner and Event Detail copy. The event name, time, and
            partner venue (the location name, or the hosting organization if no
            venue is set) are filled in automatically from this event. Nothing
            to edit here.
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">
              Additional event information shown on the landing page. Leave
              blank to use the event description.
            </p>
            <RichTextEditor
              content={eventDetailsHtml}
              onChange={setEventDetailsHtml}
              placeholder="Enter event details..."
            />
          </>
        )}
      </div>

      {/* Contact Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Contact Card</h4>
        <p className="text-xs text-gray-500 mb-3">
          Displayed in the sidebar of the landing page. USLS defaults are
          pre-filled.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Department
            </label>
            <input
              type="text"
              value={contactDepartment}
              onChange={(e) => setContactDepartment(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Events Department"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Phone
            </label>
            <input
              type="text"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="855-379-8994"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Email
            </label>
            <input
              type="text"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Seminars@USLawShield.com"
            />
          </div>
        </div>
      </div>

      {/* Registration Limit & Pricing */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Registration &amp; Pricing
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Registration Limit
            </label>
            <input
              type="number"
              min="0"
              value={registrationLimit}
              onChange={(e) => setRegistrationLimit(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Unlimited"
            />
            <p className="text-xs text-gray-400 mt-1">
              Max registrations
              {registrationLimit ? `: ${registrationLimit}` : " (unlimited)"}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Member Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={memberPrice}
              onChange={(e) => setMemberPrice(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Non-Member Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={nonMemberPrice}
              onChange={(e) => setNonMemberPrice(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Pricing Label
            </label>
            <input
              type="text"
              value={pricingLabel}
              onChange={(e) => setPricingLabel(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Early Bird Pricing"
            />
          </div>
        </div>
      </div>

      {/* How Did You Hear Options */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          "How Did You Hear About Us?" Options
        </h4>
        <div className="flex flex-wrap gap-2 mb-3">
          {hearAboutOptions.map((option, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
            >
              {option}
              <button
                onClick={() => handleRemoveHearOption(index)}
                className="text-gray-400 hover:text-red-500"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newHearOption}
            onChange={(e) => setNewHearOption(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddHearOption()}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Add new option..."
          />
          <Button variant="secondary" size="sm" onClick={handleAddHearOption}>
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Registration List */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-1">
          Registration List
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Each successful registration adds the contact to this reusable CRM
          list. Only static lists can receive contacts.{" "}
          <strong>
            You can’t publish until you either choose a list or check the box
            below
          </strong>{" "}
          — so a live sign-up page never collects registrants you have no way to
          follow up with.
        </p>
        {registrationList ? (
          <div className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-3 py-2">
            <span className="text-sm text-green-900">
              ✓ <strong>{registrationList.name}</strong>
              {typeof registrationList.contact_count === "number"
                ? ` · ${registrationList.contact_count} contacts`
                : ""}
              {registrationList.is_dynamic
                ? " — ⚠ dynamic list; registrants can't be added"
                : ""}
            </span>
            <button
              onClick={handleClearList}
              disabled={isListBusy}
              className="text-xs font-medium text-red-600 hover:text-red-800 disabled:text-gray-400"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <select
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select an existing static list…</option>
                {(staticLists || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {typeof l.contact_count === "number"
                      ? ` (${l.contact_count})`
                      : ""}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                size="sm"
                disabled={!selectedListId || isListBusy}
                onClick={() => handleSelectExistingList(selectedListId)}
              >
                Use list
              </Button>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={isListBusy}
              onClick={handleCreateList}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Create "{event.title} Registrants"
            </Button>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={noListAck}
                onChange={(e) => setNoListAck(e.target.checked)}
              />
              Don't add registrants to a list (not recommended — no one who
              signs up will be saved to a list you can email or follow up with)
            </label>
          </div>
        )}
      </div>

      {/* Audience */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-1">Audience</h4>
        <p className="text-xs text-gray-500 mb-3">
          Members-only hides the non-member registration option and uses
          member-focused copy on the public page.
        </p>
        <div className="flex gap-2">
          {(["members", "public"] as const).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAudienceType(a)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                audienceType === a
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {a === "members"
                ? "Members only"
                : "Public (members & non-members)"}
            </button>
          ))}
        </div>
        <p className="text-xs text-amber-600 mt-2">
          Save Changes to apply the audience setting.
        </p>
      </div>

      {/* Publish workflow */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-gray-900">Publish</h4>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_META[readiness.status].cls}`}
              >
                {STATUS_META[readiness.status].label}
              </span>
            </div>
            {!isPublished && readiness.blockers.length > 0 && (
              <ul className="mt-1 text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                {readiness.blockers.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
            {readiness.warnings.map((w, i) => (
              <p key={i} className="mt-1 text-xs text-amber-600">
                ⚠ {w}
              </p>
            ))}
            {readiness.status === "published_unavailable" && (
              <p className="mt-1 text-xs text-red-700">
                Published, but the event is cancelled/completed or registration
                is closed — the live page won't accept registrations.
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Preview is for review only. The live URL works only after
              publishing.
            </p>
          </div>

          <div className="flex flex-col items-stretch gap-2 shrink-0">
            {!isPublished ? (
              <Button
                variant="primary"
                size="sm"
                disabled={
                  !landingPage || isPublishing || readiness.blockers.length > 0
                }
                onClick={handlePublish}
              >
                {isPublishing ? "Publishing…" : "Publish Page"}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={isPublishing}
                onClick={handleUnpublish}
              >
                {isPublishing ? "Working…" : "Unpublish Page"}
              </Button>
            )}
            {landingPage && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50 transition-colors"
              >
                <EyeIcon className="w-4 h-4" />
                Preview
              </a>
            )}
            {landingPage && isPublished && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(fullUrl);
                  showToast("success", "Live URL copied to clipboard");
                }}
                className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <ClipboardIcon className="w-4 h-4" />
                Copy Live URL
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Public member self check-in (independent of the landing page) */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Public member self check-in
            </h3>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              When enabled, a walk-in member can check themselves in from a
              public page by confirming their identity (email or member #, plus
              last name and ZIP). If this event has a related voucher offer,
              confirmed members receive it by email to their address on file.
              Making this page public implies trust — leave it off to handle
              walk-ins with staff only.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleSelfCheckin}
            disabled={selfCheckinSaving}
            role="switch"
            aria-checked={selfCheckinEnabled}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              selfCheckinEnabled ? "bg-primary-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                selfCheckinEnabled ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {selfCheckinEnabled && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <code className="text-xs bg-gray-100 rounded px-2 py-1 text-gray-700 break-all">
              {selfCheckinUrl}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(selfCheckinUrl);
                showToast("success", "Self check-in URL copied to clipboard");
              }}
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <ClipboardIcon className="w-4 h-4" />
              Copy link
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Landing Page
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this landing page? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageTab;
